/*
  File: italiansecret-stock-guard.js
  Purpose:
  Proactive stock guard for the order grid + checkout gate in sticky cart.
  - Grays out products that would exceed component inventory.
  - Blocks checkout when cart requirements exceed inventory.
*/

(function () {
  if (window.IS_STOCK_GUARD_ENABLED === false) {
    return;
  }
  const WARNING_CLASS = "is-stock-guard-warning";
  const CHECKOUT_SELECTOR = ".is-sticky-cart__primary[href='/checkout']";
  const DELIVERY_KEY = "italiansecret_delivery_date";
  const AVAILABILITY_URL =
    window.IS_STOCK_GUARD_URL ||
    "https://storage.googleapis.com/italiansecret/availability.json";
  const AVAILABILITY_TIMEOUT = Number(window.IS_STOCK_GUARD_TIMEOUT_MS || 4000);

  function parseJson(id) {
    try {
      const el = document.querySelector(id);
      if (!el) return {};
      return JSON.parse(el.textContent || "{}");
    } catch (err) {
      console.warn("ItalianSecretStockGuard: invalid JSON in", id, err);
      return {};
    }
  }

  const includedMap = window.includedMap || parseJson("#is-included-map");
  const stockMap = window.stockMap || parseJson("#is-stock-map");
  const gidMap = window.gidMap || parseJson("#is-gid-map");
  const nameMap = window.nameMap || parseJson("#is-name-map");

  if (!window.includedMap) window.includedMap = includedMap;
  if (!window.stockMap) window.stockMap = stockMap;
  if (!window.gidMap) window.gidMap = gidMap;
  if (!window.nameMap) window.nameMap = nameMap;

  let activeStockMap = stockMap;
  let availabilityPayload = null;
  let availabilityPromise = null;

  function normalizeDate(value) {
    if (!value) return null;
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) return null;
    return parsed.toISOString().slice(0, 10);
  }

  function getDeliveryDate() {
    return normalizeDate(sessionStorage.getItem(DELIVERY_KEY));
  }

  function pickAvailabilityDate(availabilityByDate, deliveryDate) {
    if (deliveryDate && availabilityByDate[deliveryDate]) return deliveryDate;
    const dates = Object.keys(availabilityByDate || {}).sort();
    return dates.length ? dates[0] : null;
  }

  function refreshActiveStockMap() {
    if (!availabilityPayload || availabilityPayload.enabled === false) {
      activeStockMap = availabilityPayload ? {} : stockMap;
      return;
    }
    const availableByDate = availabilityPayload.available_by_date || {};
    const targetDate = pickAvailabilityDate(availableByDate, getDeliveryDate());
    activeStockMap = availableByDate[targetDate] || stockMap;
  }

  async function loadAvailability() {
    if (!AVAILABILITY_URL) return null;
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), AVAILABILITY_TIMEOUT);
      const res = await fetch(AVAILABILITY_URL, {
        cache: "no-cache",
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!res.ok) return null;
      const data = await res.json();
      if (!data || typeof data !== "object") return null;
      availabilityPayload = data;
      refreshActiveStockMap();
      return data;
    } catch (err) {
      console.warn("ItalianSecretStockGuard: availability fetch failed", err);
      return null;
    }
  }

  function ensureAvailability() {
    if (!availabilityPromise) {
      availabilityPromise = loadAvailability();
    }
    return availabilityPromise;
  }

  function gidToSku(gid) {
    if (!gid || typeof gid !== "string") return null;
    if (gid.includes("/ProductVariant/") && gidMap[gid]) return gidMap[gid];
    if (gid.includes("/Product/") && !gid.includes("/ProductVariant/")) {
      const id = gid.split("/Product/")[1];
      const variantKey = `gid://shopify/ProductVariant/${id}`;
      if (gidMap[variantKey]) return gidMap[variantKey];
      if (gidMap[gid]) return gidMap[gid];
    }
    return gidMap[gid] || null;
  }

  function getAvailable(sku) {
    if (!sku) return null;
    if (!Object.prototype.hasOwnProperty.call(activeStockMap, sku)) return null;
    const qty = Number(activeStockMap[sku]);
    return Number.isFinite(qty) ? qty : 0;
  }

  function buildRequirementsFromCart(cart) {
    const required = {};
    (cart.items || []).forEach(item => {
      const sku = (item.sku || "").trim();
      const qty = Number(item.quantity) || 0;
      if (!sku || qty <= 0) return;

      // direct SKU consumption (toppings/bases tracked in stock map)
      if (Object.prototype.hasOwnProperty.call(stockMap, sku)) {
        required[sku] = (required[sku] || 0) + qty;
      }

      // pizza: add included toppings
      const included = includedMap[sku];
      if (Array.isArray(included) && included.length) {
        included.forEach(gid => {
          const toppingSku = gidToSku(gid);
          if (!toppingSku) return;
          required[toppingSku] = (required[toppingSku] || 0) + qty;
        });
      }
    });
    return required;
  }

  function getCardSku(card) {
    const isPizza = card.getAttribute("data-is-pizza") === "true";
    const mode = card.getAttribute("data-card-mode") || "";
    if (isPizza) {
      if (mode === "gf") return card.getAttribute("data-gf-sku") || null;
      return (
        card.getAttribute("data-regular-sku") ||
        card.getAttribute("data-gf-sku") ||
        null
      );
    }
    return card.getAttribute("data-single-sku") || null;
  }

  function isCardAvailable(card, requirements) {
    const sku = getCardSku(card);
    if (!sku) return true;

    const isPizza = card.getAttribute("data-is-pizza") === "true";
    const included = includedMap[sku];

    if (isPizza && Array.isArray(included) && included.length) {
      for (const gid of included) {
        const toppingSku = gidToSku(gid);
        if (!toppingSku) continue;
        const available = getAvailable(toppingSku);
        if (available === null) continue;
        const needed = (requirements[toppingSku] || 0) + 1;
        if (needed > available) return false;
      }
      return true;
    }

    const available = getAvailable(sku);
    if (available === null) return true;
    const needed = (requirements[sku] || 0) + 1;
    return needed <= available;
  }

  function setCardState(card, available) {
    const addBtn = card.querySelector(".js-order-add-to-cart");
    const plusBtn = card.querySelector(".js-order-qty-plus");
    const qtyBox = card.querySelector(".is-order-card__qty");

    if (available) {
      card.classList.remove("is-order-card--unavailable");
      if (addBtn) {
        addBtn.disabled = false;
        addBtn.setAttribute("aria-disabled", "false");
      }
      if (plusBtn) plusBtn.disabled = false;
    } else {
      card.classList.add("is-order-card--unavailable");
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.setAttribute("aria-disabled", "true");
      }
      if (plusBtn) plusBtn.disabled = true;
      if (qtyBox) qtyBox.classList.remove("hidden");
    }
  }

  async function refreshGridAvailability() {
    const grid = document.querySelector("[data-is-order-grid]");
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll("[data-order-card]"));
    if (!cards.length) return;

    await ensureAvailability();
    refreshActiveStockMap();

    const res = await fetch("/cart.js", { cache: "no-cache" });
    if (!res.ok) return;
    const cart = await res.json();
    const requirements = buildRequirementsFromCart(cart);

    cards.forEach(card => {
      const available = isCardAvailable(card, requirements);
      setCardState(card, available);
    });
  }

  function computeShortages(requirements) {
    const shortages = [];
    Object.keys(requirements).forEach(sku => {
      const needed = requirements[sku];
      const available = getAvailable(sku);
      if (available === null) return;
      if (needed > available) {
        shortages.push({
          sku,
          needed,
          available,
          name: nameMap[sku] || sku,
        });
      }
    });
    return shortages;
  }

  function renderStickyWarning(shortages) {
    const panel = document.querySelector(".is-sticky-cart__panel");
    if (!panel) return;
    panel.querySelector(`.${WARNING_CLASS}`)?.remove();
    if (!shortages.length) return;

    const box = document.createElement("div");
    box.className = WARNING_CLASS;
    box.innerHTML = `
      <strong>Some ingredients are out of stock.</strong>
      <div class="is-stock-guard-warning__list">
        ${shortages
          .map(item => `${item.name} (${item.needed - item.available} missing)`)
          .join(", ")}
      </div>
    `;
    const footer = panel.querySelector(".is-sticky-cart__footer");
    footer ? footer.prepend(box) : panel.prepend(box);
  }

  async function guardCheckout(e) {
    await ensureAvailability();
    refreshActiveStockMap();

    const res = await fetch("/cart.js", { cache: "no-cache" });
    if (!res.ok) return;
    const cart = await res.json();
    const requirements = buildRequirementsFromCart(cart);
    const shortages = computeShortages(requirements);

    if (shortages.length) {
      e.preventDefault();
      renderStickyWarning(shortages);
      alert("Some ingredients are out of stock. Please adjust your order before checkout.");
    }
  }

  document.addEventListener("DOMContentLoaded", () => {
    ensureAvailability().finally(refreshGridAvailability);

    document.addEventListener("italiansecret:delivery-date-selected", () => {
      refreshActiveStockMap();
      refreshGridAvailability();
    });

    document.addEventListener("cart:updated", refreshGridAvailability);
    document.addEventListener("cart-updated", refreshGridAvailability);
    document.documentElement.addEventListener("is:cart:updated", refreshGridAvailability);

    const checkoutBtn = document.querySelector(CHECKOUT_SELECTOR);
    if (checkoutBtn) {
      checkoutBtn.addEventListener("click", guardCheckout);
    }
  });
})();
